import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    CheckCircle2,
    Clock,
    Truck,
    XCircle,
    AlertCircle,
    Package,
    RotateCcw
} from 'lucide-react';

/**
 * Map internal status enum → Sendit dot/bubble color palette.
 * Red: Failed/Delayed/Canceled
 * Green: Out for delivery/Delivered
 * Blue: Transit/Warehouse/Created
 * Orange: Picking/Return
 */
const getStatusConfig = (status, rawStatus) => {
    // Standard color groupings based on Sendit visuals
    const CONFIGS = {
        'DELIVERED': { color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', dot: 'bg-emerald-500' },
        'RETURNED': { color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200', dot: 'bg-orange-500' },
        'CANCELLED': { color: 'text-rose-600', bg: 'bg-rose-50', border: 'border-rose-200', dot: 'bg-rose-500' },
        'OUT_FOR_DELIVERY': { color: 'text-emerald-500', bg: 'bg-emerald-50', border: 'border-emerald-100', dot: 'bg-emerald-500' },
        'IN_TRANSIT': { color: 'text-sky-600', bg: 'bg-sky-50', border: 'border-sky-200', dot: 'bg-sky-500' },
        'NEW_PARCEL': { color: 'text-slate-600', bg: 'bg-slate-50', border: 'border-slate-200', dot: 'bg-slate-500' },
        'CREATED': { color: 'text-slate-400', bg: 'bg-slate-50', border: 'border-slate-200', dot: 'bg-slate-300' },
        'DELAYED': { color: 'text-rose-500', bg: 'bg-rose-50', border: 'border-rose-200', dot: 'bg-rose-500' }, // Sendit uses red for delayed/reported
        'FAILED': { color: 'text-rose-600', bg: 'bg-rose-50', border: 'border-rose-200', dot: 'bg-rose-600' },
        'PICKUP': { color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200', dot: 'bg-amber-500' }
    };

    if (CONFIGS[status]) return CONFIGS[status];

    // Fallback based on raw keywords
    const s = (rawStatus || status || '').toLowerCase();
    if (s.includes('livr')) return CONFIGS.DELIVERED;
    if (s.includes('retour')) return CONFIGS.RETURNED;
    if (s.includes('annul') || s.includes('refus')) return CONFIGS.CANCELLED;
    if (s.includes('distribution') || s.includes('cours de livraison')) return CONFIGS.OUT_FOR_DELIVERY;
    if (s.includes('transit') || s.includes('entrepôt')) return CONFIGS.IN_TRANSIT;
    if (s.includes('ramassage') || s.includes('ramass') || s.includes('pick')) return CONFIGS.PICKUP;
    if (s.includes('report')) return CONFIGS.DELAYED;

    return { color: 'text-slate-400', bg: 'bg-slate-50', border: 'border-slate-100', dot: 'bg-slate-300' };
};

const TrackingTimeline = ({ history = [], currentStatus }) => {
    const { t, i18n } = useTranslation();
    
    // Sort ascending (oldest first)
    const items = [...history].sort((a, b) => new Date(a.at) - new Date(b.at));
    
    // If no history, show current status
    const displayItems = items.length > 0
        ? items
        : (currentStatus ? [{ status: currentStatus, label: currentStatus, at: new Date() }] : []);

    if (displayItems.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-12 text-slate-300">
                <Package size={40} strokeWidth={1} className="mb-3 opacity-30" />
                <p className="text-[10px] font-bold uppercase tracking-widest">Aucune donnée de suivi</p>
            </div>
        );
    }

    // Sort descending for display (newest at top) to match Sendit's flow
    const sortedItems = [...displayItems].reverse();

    return (
        <div className="relative pl-6 py-4">
            {/* Vertical Line */}
            <div className="absolute left-[31px] top-6 bottom-6 w-[1px] bg-slate-100" />

            <div className="space-y-10">
                {sortedItems.map((item, idx) => {
                    const config = getStatusConfig(item.status, item.label || item.rawStatus);
                    
                    // Priority: label (French raw string) > rawStatus > status (Enum)
                    const title = item.label || item.rawStatus || item.status || 'Statut';
                    const actor = item.etat || ''; // Includes "par ..."

                    return (
                        <div key={idx} className="relative group">
                            {/* Dot */}
                            <div className={`absolute -left-[3px] top-4 w-2.5 h-2.5 rounded-full border-2 border-white ring-1 ring-slate-100 z-10 ${config.dot} shadow-sm transition-transform group-hover:scale-125`} />

                            <div className="flex flex-col gap-1.5 translate-y-[-2px]">
                                {/* Header: Status Bubble + Actor */}
                                <div className="flex flex-wrap items-center gap-2">
                                    <div className={`px-2.5 py-1 rounded-md border ${config.bg} ${config.border} ${config.color} text-[11px] font-bold shadow-sm inline-flex items-center gap-1.5`}>
                                        {title}
                                    </div>
                                    
                                    {actor && (
                                        <div className="text-[11px] text-slate-400 font-medium">
                                            {actor.startsWith('par') ? ' ' : 'par '}
                                            <span className="font-bold text-slate-600">{actor.replace(/^par\s+/i, '')}</span>
                                        </div>
                                    )}
                                </div>

                                {/* Metadata: Date & Clock */}
                                <div className="flex items-center gap-2 text-slate-400">
                                    <Clock size={12} strokeWidth={2.5} className="opacity-60" />
                                    <span className="text-[10px] font-semibold capitalize">
                                        {item.at
                                            ? new Date(item.at).toLocaleString('fr-FR', {
                                                weekday: 'long',
                                                day: 'numeric',
                                                month: 'long',
                                                year: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            }).replace(' à ', ', à ')
                                            : '-- -- --'}
                                    </span>
                                </div>

                                {/* Comment / Details */}
                                {item.comment && item.comment.toLowerCase() !== 'updated' && (
                                    <p className="text-[11px] text-slate-500 font-medium leading-relaxed pl-1 max-w-sm border-l-2 border-slate-50 mt-1">
                                        {item.comment}
                                    </p>
                                )}

                                {/* Location */}
                                {item.location && (
                                    <div className="text-[9px] font-black text-sky-500 uppercase tracking-wider flex items-center gap-1.5 mt-0.5">
                                        <div className="w-1 h-1 rounded-full bg-sky-400" />
                                        {item.location}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default TrackingTimeline;
